import { useDownloadPublicPaymentReceiptPdfMutation } from '@massilia/queries'
import { useMemo } from 'react'

import type { PurchasePayload } from '@/lib/gtm'

import { PaymentReceiptActions } from '@/components/payment-confirmation/payment-receipt-actions'
import { useTrackPurchaseOnce } from '@/hooks/use-track-purchase-once'

import { SimplePaymentConfirmation } from './simple-payment-confirmation'

// Cet écran est le retour de TOUS les règlements passés hors tunnel depuis /payer-acompte :
// l'acompte en carte bancaire, mais aussi le solde réglé en 3/4x. Les libellés restent donc neutres —
// parler d'« acompte » serait faux pour un règlement du solde en plusieurs fois.
const DEPOSIT_STRINGS = {
  failedDescription:
    "Votre paiement n'a pas pu être traité. Veuillez réessayer ou contacter votre banque si le problème persiste.",
  failedTitle: 'Paiement échoué',
  successDescriptionBuilder: (amountLabel?: string, referenceLabel?: string) =>
    `Votre paiement${amountLabel ? ` de ${amountLabel}` : ''}${referenceLabel ?? ''} a bien été enregistré. Vous recevrez une confirmation par email sous peu.`,
  successTitle: 'Paiement reçu !',
  unknownDescription:
    "Nous n'avons pas pu confirmer l'état de votre paiement. Vérifiez votre espace client ou contactez notre équipe si besoin.",
  unknownTitle: 'Statut de paiement indisponible',
} as const

interface DepositPaymentConfirmationProps {
  transId?: string
  amountCents?: number
  reference?: string
  status: 'failed' | 'success' | 'unknown'
}

export function DepositPaymentConfirmation({
  transId,
  amountCents,
  reference,
  status,
}: DepositPaymentConfirmationProps) {
  const downloadReceiptMutation = useDownloadPublicPaymentReceiptPdfMutation()

  // Conversion Google Ads : l'acompte compte avec le montant effectivement payé.
  // transaction_id suffixé `-acompte` pour ne pas entrer en collision avec le
  // push du solde (réservation complète) qui réutilise la même référence.
  const purchasePayload = useMemo<null | PurchasePayload>(() => {
    if (status !== 'success' || !reference || amountCents === null || amountCents === undefined)
      return null
    const value = amountCents / 100
    return {
      transactionId: `${reference}-acompte`,
      currency: 'EUR',
      items: [{ item_name: `Acompte réservation ${reference}`, price: value, quantity: 1 }],
      value,
    }
  }, [status, reference, amountCents])
  useTrackPurchaseOnce(purchasePayload)

  const handleDownloadReceipt = async () => {
    if (!transId) {
      throw new Error('missing transId')
    }
    return downloadReceiptMutation.mutateAsync(transId)
  }

  const extraActions = transId ? (
    <PaymentReceiptActions
      isLoading={downloadReceiptMutation.isPending}
      onDownload={handleDownloadReceipt}
    />
  ) : undefined

  return (
    <SimplePaymentConfirmation
      amountCents={amountCents}
      extraActions={extraActions}
      reference={reference}
      retryLink="/payer-acompte"
      status={status}
      strings={DEPOSIT_STRINGS}
    />
  )
}
